import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Col, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import useApi from 'hooks/useApi';
import { setUploadDocuments } from 'redux/actions/documentActions';
import { showAlert } from 'redux/actions/alertActions';

import { Switch } from 'shared/components/form/Inputs';
import { colors } from 'shared/defaultTheme';
import { Button } from 'shared/components/Buttons';
import MultipleSelectSearch from 'shared/components/MultipleSelectSearch';
import { LabelRequiredInput, LabelError } from 'shared/components/Labels';
import {
  ALERT_TYPE_ERROR,
  ALERT_TYPE_SUCCESS,
  getIconAccessDocument,
  getIconCode,
  getIconDoc,
  getIconExcel,
  getIconImage,
  getIconPdf,
  getIconPpt,
  getIconSound,
  getIconVideo,
  getIconZip,
} from 'shared/utils';
import MainSpinner from 'shared/components/MainSpinner';
import SwitchEmailToContacts from 'shared/components/SwitchEmailToContacts';

import { ReactComponent as DocumentIcon } from 'assets/icon-document.svg';
import { ReactComponent as PdfIcon } from 'assets/icon-pdf.svg';
import { ReactComponent as WordIcon } from 'assets/icon-word.svg';
import { ReactComponent as ImgIcon } from 'assets/icon-img.svg';
import { ReactComponent as ExcelIcon } from 'assets/icon-excel.svg';
import { ReactComponent as HtmlIcon } from 'assets/icon-html.svg';
import { ReactComponent as SoundIcon } from 'assets/icon-sound.svg';
import { ReactComponent as VideoIcon } from 'assets/icon-video.svg';
import { ReactComponent as AccessDocumentIcon } from 'assets/icon-access-document.svg';
import { ReactComponent as PowerPointIcon } from 'assets/icon-ppt.svg';
import { ReactComponent as ZipIcon } from 'assets/icon-zip.svg';

import { PublicDocumentsWrapper } from '../styles';

const PublicDocuments = ({ onCancel, setShowConfirmPublic, setConfirmMessage }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { customAreaApi } = useApi();
  const { uploadDocuments, emailToContactsOnPublish } = useSelector((state) => state.document);
  const layout = useSelector((state) => state.layout);
  const { rememberEdit } = layout;
  const { cusNavId, isCustomer, clientId, mail } = useSelector((state) => state.auth);
  const { currentClient } = useSelector((state) => state.clientState);
  const [loadingContact, setLoadingContact] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [contactsDataSource, setContactsDataSource] = useState();
  const [isAllPublic, setIsAllPublic] = useState(false);
  const [isRequired, setIsRequired] = useState(false);
  const [contactsSelectedCount, setContactsSelectedCount] = useState(0);

  const [{ loading: loadingUpdate }, updatePublicDocument] = customAreaApi(
    {
      method: 'POST',
    },
    { manual: true }
  );

  const [, getContactsData] = customAreaApi(
    {
      method: 'GET',
    },
    { manual: true }
  );

  useEffect(() => {
    setTimeout(() => {
      setLoadingContact(true);
    }, 500);
    return () => {
      setLoadingContact(false);
    };
  }, []);

  useEffect(() => {
    const getContactList = async () => {
      const {
        data: { data },
      } = await getContactsData({
        url: `${window.API_V1}/cli/contacts/${cusNavId}/${
          isCustomer ? currentClient.clientId : clientId
        }`,
      });

      const filterData = data.filter((x) => x.mail);
      setContacts(filterData);
    };
    getContactList();
  }, []);

  useEffect(() => {
    setContactsDataSource(
      contacts.map((contact) => {
        return contact.surname
          ? {
              ...contact,
              fullName: `${contact.name} ${contact.surname}`,
            }
          : { ...contact, fullName: contact.name };
      })
    );
  }, [contacts]);

  const Title = () => {
    return (
      <Row className="row-title mb-4">
        <Col sm={12} className="ml-n3">
          <h3>{t('new_document_title_public_documents')}</h3>
        </Col>
      </Row>
    );
  };

  const IconFile = ({ file }) => {
    if (getIconDoc(file.type)) {
      return <WordIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconPdf(file.type)) {
      return <PdfIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconImage(file.type)) {
      return <ImgIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconCode(file.type)) {
      return <HtmlIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconSound(file.type)) {
      return <SoundIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconVideo(file.type)) {
      return <VideoIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconAccessDocument(file.type)) {
      return (
        <AccessDocumentIcon
          fill={colors.suvaGrey}
          className="control-panel__icon-type"
          width="20"
        />
      );
    }
    if (getIconPpt(file.type)) {
      return (
        <PowerPointIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />
      );
    }
    if (getIconZip(file.type)) {
      return <ZipIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconExcel(file.type)) {
      return <ExcelIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    return <DocumentIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
  };

  IconFile.propTypes = {
    file: PropTypes.shape({
      type: PropTypes.string,
    }).isRequired,
  };

  const onPublic = (checked, uploadDocument) => {
    const newUploadDocument = uploadDocuments.map((document) => {
      if (uploadDocument.id === document.id) {
        return { ...uploadDocument, public: checked };
      }
      return { ...document };
    });
    dispatch(setUploadDocuments([...newUploadDocument]));
  };

  const selectedAllMail = (email) => {
    const newContactEmail = email.map((e) => {
      return { u: e, p: 'r--' };
    });
    const newUploadDocument = uploadDocuments.map((document) => {
      return { ...document, permissions: newContactEmail };
    });
    dispatch(setUploadDocuments([...newUploadDocument]));
  };

  const selectedMail = (email, uploadDocument) => {
    const newContactEmail = email.map((e) => {
      return { u: e, p: 'r--' };
    });
    const newUploadDocument = uploadDocuments.map((document) => {
      if (uploadDocument.id === document.id) {
        return { ...uploadDocument, permissions: newContactEmail };
      }
      return { ...document };
    });
    dispatch(setUploadDocuments([...newUploadDocument]));
  };

  const onPublicDocument = async () => {
    const newUploadDocument = uploadDocuments.map((document) => {
      if (document.permissions.length === 0 && document.public) {
        return { ...document, isRequired: true };
      }
      return { ...document, isRequired: false };
    });
    dispatch(setUploadDocuments([...newUploadDocument]));

    const existSendToEmpty = uploadDocuments.some((x) => x.permissions.length === 0 && x.public);

    if (existSendToEmpty) {
      setIsRequired(true);
    }

    if (!existSendToEmpty && !rememberEdit) {
      onCancel();
      setConfirmMessage(t('documents_document_public'));
      setShowConfirmPublic(true);
      setIsRequired(false);
    } else if (!existSendToEmpty && rememberEdit) {
      const findPublicDocument = uploadDocuments.filter((x) => x.public);
      try {
        await updatePublicDocument({
          data: isCustomer ? findPublicDocument : uploadDocuments,
          url: `${window.API_V1}/cli/items/${cusNavId}/${
            isCustomer ? currentClient.clientId : clientId
          }/${encodeURIComponent(mail)}/publish?sendNotify=${emailToContactsOnPublish.toString()}`,
        });
        dispatch(
          showAlert({
            alertType: ALERT_TYPE_SUCCESS,
            alertMessage: t('general_success_message'),
          })
        );
        setIsRequired(false);
        dispatch(setUploadDocuments([]));
        onCancel();
      } catch (error) {
        dispatch(
          showAlert({
            alertType: ALERT_TYPE_ERROR,
            alertMessage: t('custom_field_operation_error_message'),
          })
        );
        onCancel();
        dispatch(setUploadDocuments([]));
      }
    }
  };

  useEffect(() => {
    const newUploadDocument = uploadDocuments.map((document) => {
      return { ...document, public: isAllPublic, permissions: [] };
    });
    dispatch(setUploadDocuments([...newUploadDocument]));
  }, [isAllPublic]);

  useEffect(() => {
    let count = 0;

    uploadDocuments.forEach((doc) => {
      if (doc.public && doc.permissions.length > 0) {
        count += doc.permissions.length;
      }
    });

    if (isAllPublic) {
      count /= uploadDocuments.length;
    }

    setContactsSelectedCount(count);
  }, [uploadDocuments]);

  return (
    <>
      <PublicDocumentsWrapper>
        <Title />
        <div className="w-100">
          <div className="public-data-container">
            {uploadDocuments && uploadDocuments.length === 1 && (
              <h4 className="sub-title ml-n4 mb-3">
                {isCustomer
                  ? t('document_publish_document_portal_customer')
                  : t('document_publish_document_portal')}
              </h4>
            )}
            {uploadDocuments && uploadDocuments.length >= 2 && (
              <>
                <h4 className="sub-title ml-n4 mb-3">
                  {isCustomer
                    ? t('document_publish_documents_portal_customer')
                    : t('document_publish_documents_portal')}
                </h4>
                <div className="mb-4">
                  <div className="d-flex mb-3">
                    <span className="mr-2">{t('documents_all_file')}</span>
                    <Switch
                      id="all-public"
                      onChange={(checked) => setIsAllPublic(checked)}
                      value={!isAllPublic}
                      color={isAllPublic ? colors.pastelGreen : colors.venetianRed}
                    />
                  </div>

                  {isAllPublic && (
                    <div>
                      <LabelRequiredInput
                        label={t('document_contact_permissions')}
                        color={colors.black}
                        className="mb-1"
                        error={isRequired}
                      />
                      <MultipleSelectSearch
                        dataSource={contactsDataSource}
                        fields={{ text: 'fullName', value: 'mail' }}
                        placeholder={t('general_all')}
                        onChange={(email) => selectedAllMail(email)}
                      />
                      {isRequired && <LabelError label={t('general_required_field')} />}
                    </div>
                  )}
                </div>
              </>
            )}
            <div className="list-public-container">
              {uploadDocuments.map((uploadDocument) => (
                <React.Fragment key={uploadDocument.id}>
                  <div className="d-flex mb-3">
                    <IconFile file={uploadDocument} />
                    <div className="d-flex justify-content-between container-public align-items-center">
                      <p className="upload-name ml-1">{uploadDocument.name}</p>
                      {!isAllPublic && (
                        <Switch
                          id={`public-${uploadDocument.id}`}
                          onChange={(checked) => onPublic(checked, uploadDocument)}
                          value={!uploadDocument.public}
                          color={uploadDocument.public ? colors.pastelGreen : colors.venetianRed}
                        />
                      )}
                    </div>
                  </div>
                  {uploadDocument.public && !isAllPublic && (
                    <div className="mb-4">
                      <LabelRequiredInput
                        label={t('document_contact_permissions')}
                        color={colors.black}
                        className="mb-1"
                        error={uploadDocument.isRequired}
                      />
                      {loadingContact && (
                        <MultipleSelectSearch
                          dataSource={contactsDataSource}
                          fields={{ text: 'fullName', value: 'mail' }}
                          placeholder={t('general_all')}
                          onChange={(email) => selectedMail(email, uploadDocument)}
                        />
                      )}
                      {uploadDocument.isRequired && (
                        <LabelError label={t('general_required_field')} />
                      )}
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
          {isCustomer && <SwitchEmailToContacts selectedContacts={contactsSelectedCount} />}
          <div className="d-flex justify-content-end pt-3">
            <Button
              className="text-uppercase"
              type="button"
              label={t('general_button_ok')}
              color="primary"
              onClick={() => onPublicDocument()}
            />
          </div>
          {loadingUpdate && <MainSpinner />}
        </div>
      </PublicDocumentsWrapper>
    </>
  );
};

PublicDocuments.defaultProps = {};

PublicDocuments.propTypes = {
  onCancel: PropTypes.func.isRequired,
  setShowConfirmPublic: PropTypes.func.isRequired,
  setConfirmMessage: PropTypes.func.isRequired,
};

export default PublicDocuments;
